// Single source of truth for tool URL paths.
//
// Why this exists:
//   We originally namespaced every tool under /box/<slug> — a convenient
//   prefix when the catalogue was almost all box generators, but a
//   terrible SEO signal for /box/luminaire, /box/neon-sign,
//   /box/vectorize, /box/gear-builder etc. (Google reads the path as
//   "this is a box page", which is wrong for 25+ tools.)
//
//   The new canonical URLs are flat, keyword-rich and human-readable:
//     /basic-box-generator       (was /box/basic)
//     /cubby-shelf-generator     (was /box/cubby)
//     /luminaire-generator       (was /box/luminaire)
//     /gear-generator            (was /box/gear-builder)
//     /vectorize-generator       (was /box/vectorize)
//     /bend-calculator           (was /box/bend-calculator — kept tight,
//                                 it's a calculator, not a generator)
//
// Migration strategy: ADDITIVE, NOT REPLACING.
//   • Both paths route to the same component (see App.js).
//   • <Seo canonical> emits ONLY the new path, so Google consolidates
//     ranking signals on the new URL over time (typically 4–8 weeks).
//   • Sitemap.xml only lists the new path.
//   • User-facing internal links (Home grid, Examples, Vs CTAs)
//     gradually migrate to the new path — but the legacy /box/<slug>
//     keeps working forever so external backlinks, bookmarks, embedded
//     iframes and analytics history don't break.

/**
 * Canonical slug → URL path map.
 *
 * This is the AUTHORITATIVE list of every public tool URL. Adding a
 * new tool means: (1) add its catalogue entry to mock.js, (2) add an
 * entry here mapping the internal slug to its public path, and (3) add
 * the matching <Route> in App.js. Steps (2) and (3) are intentionally
 * separate so the SEO map and the routing tree stay reviewable.
 */
const CANONICAL_PATH = {
  // ── Configurator-driven tools ──────────────────────────────────────
  basic:              '/basic-box-generator',
  cardboard:          '/cardboard-box-generator',
  cubby:              '/cubby-shelf-generator',
  curved:             '/curved-box-generator',
  divider:            '/divider-box-generator',
  grid:               '/grid-box-generator',
  hinge:              '/hinged-box-generator',
  kerf:               '/kerf-calibration',       // calibration aid
  focus:              '/laser-focus-test',        // calibration aid
  luminaire:          '/luminaire-generator',
  'luminaire-shapes': '/luminaire-shapes-generator',
  maze:               '/maze-generator',
  panel:              '/decorative-panel-generator',
  polygon:            '/polygon-box-generator',
  puzzle:             '/puzzle-generator',
  slide:              '/sliding-lid-box-generator',
  ballot:             '/ballot-box-generator',

  // ── Dedicated-page tools ──────────────────────────────────────────
  vectorize:          '/vectorize-generator',
  'image-puzzle':     '/image-puzzle-generator',
  'photo-dots':       '/photo-dots-generator',
  'gear-builder':     '/gear-generator',
  'bend-calculator':  '/bend-calculator',         // calculator
  'l-bracket':        '/l-bracket-generator',
  enclosure:          '/enclosure-generator',
  'rack-panel':       '/rack-panel-generator',
  'slice-stack':      '/slice-stack-generator',
  'fill-pattern':     '/fill-pattern-generator',
  'connected-text':   '/connected-text-generator',
  'single-line-text': '/single-line-text-generator',
  'stir-stick':       '/stir-stick-generator',
  'neon-sign':        '/neon-sign-generator',
  'logo-to-neon':     '/logo-to-neon-generator',
  'led-pixel-board':  '/led-pixel-board-generator',
  'board-case':       '/board-case-generator',
  'ai-dxf-gen':       '/ai-dxf-generator',
  'polygon-3d':       '/polygon-3d-generator',
  display:            '/display-generator',
  'speaker-sealed':   '/sealed-speaker-generator',
  'qrcode':           '/qrcode-generator',
  'tubular-builder':  '/tube-profile-designer',
  'tube-nesting':     '/tube-nesting',
  'acm-tray':         '/acm-tray-generator',
  'facade-panel':     '/facade-panel-generator',
  'channel-letter':   '/channel-letter-generator',
  'facade-frame':     '/facade-frame-generator',
  'parts-library':    '/parts-library-generator',
  'geodesic-dome':    '/geodesic-dome-generator',
};

/**
 * Canonical URL path for a tool slug.
 *
 *   toolPath('basic')          // → '/basic-box-generator'
 *   toolPath('cubby')          // → '/cubby-shelf-generator'
 *   toolPath('bend-calculator')// → '/bend-calculator'
 *
 * Falls back to /box/<slug> for unknown slugs (defensive — should
 * only happen during the lifetime of a draft tool not yet added to
 * the map). Returns '/' for falsy / empty input.
 */
export function toolPath(slug) {
  if (!slug || typeof slug !== 'string') return '/';
  const clean = slug.trim().toLowerCase();
  return CANONICAL_PATH[clean] || `/box/${clean}`;
}

/**
 * Legacy /box/<slug> path. Kept as a working alias for backlinks,
 * bookmarks and external embeds. New code should NOT use this — it
 * exists for the route registration in App.js (and the test suite,
 * eventually).
 */
export function legacyToolPath(slug) {
  if (!slug || typeof slug !== 'string') return '/';
  return `/box/${slug.trim().toLowerCase()}`;
}

/**
 * Surface every (slug, canonical) pair. Used by the sitemap generator
 * and any audit script that needs to walk the catalogue.
 */
export function allToolPaths() {
  return Object.entries(CANONICAL_PATH).map(([slug, path]) => ({ slug, path }));
}
